import { useHotkeys } from 'react-hotkeys-hook'
import { useEffect, useState } from 'react'
import { useIsMobile } from './use-mobile'

// Define types for the hotkey mapping
export type HotkeyAction = () => void
export type HotkeyMap = Record<string, HotkeyAction>

/**
 * Custom hook for handling keyboard shortcuts in the application
 * Only activates when:
 * 1. User is on desktop (not mobile)
 * 2. User is not currently typing in an input, textarea, or contenteditable element
 * 3. User has not disabled keyboard shortcuts in settings (if applicable)
 * 
 * @param hotkeyMap - Object mapping keys to action functions
 * @param options - Additional options for hotkey behavior
 * @returns void
 */
export function useAppHotkeys(
    hotkeyMap: HotkeyMap,
    options: {
        enabled?: boolean; // Allow disabling all hotkeys conditionally
        excludeInputs?: boolean; // Whether to exclude when focused on input elements (default: true)
    } = {}
) {
    const { enabled = true, excludeInputs = true } = options
    const isMobile = useIsMobile()
    
    // Register each hotkey with the react-hotkeys-hook
    Object.entries(hotkeyMap).forEach(([key, action]) => {
        useHotkeys(
            key,
            (event: KeyboardEvent) => {
                // Only trigger if we're enabled and not on mobile
                if (!enabled || isMobile) return
                
                // Prevent default to avoid browser actions
                event.preventDefault()
                action()
            },
            // Use dependency array as third argument
            [enabled, isMobile, excludeInputs],
            // Options go in fourth argument in newer versions
            {
                enabled: enabled && !isMobile,
                enableOnFormTags: !excludeInputs
            }
        )
    })
} 